/**
 * Pack picker: given a target m² and a list of pack sizes (each with coverage
 * + price), return the most cost-efficient combination.
 *
 * Strategy:
 *   - If any pack has a null price, fall back to "minimum waste" (greedy by
 *     coverage descending), since we can't optimise cost without numbers.
 *   - If all packs have prices, use dynamic programming to find the cheapest
 *     combination whose total coverage ≥ target.
 *
 * Returns: { picks: [{label, size, coverage_m2, price, count}], totalCoverage, totalCost, hasPrice }
 */
export function pickPacks(packs, targetM2) {
  if (!packs || packs.length === 0 || !targetM2 || targetM2 <= 0) {
    return { picks: [], totalCoverage: 0, totalCost: 0, hasPrice: false };
  }

  const hasPrice = packs.every((p) => typeof p.price === 'number' && p.price >= 0);

  if (!hasPrice) {
    return greedyMinimumWaste(packs, targetM2);
  }

  return dpMinimumCost(packs, targetM2);
}

/** Greedy fallback — largest pack first, fill remainder with smaller packs. */
function greedyMinimumWaste(packs, targetM2) {
  const sorted = [...packs].sort((a, b) => b.coverage_m2 - a.coverage_m2);
  const counts = new Map();
  let covered = 0;

  // Use largest packs to cover bulk
  for (const p of sorted) {
    while (covered + p.coverage_m2 <= targetM2) {
      counts.set(p.label, (counts.get(p.label) || 0) + 1);
      covered += p.coverage_m2;
    }
  }
  // If still short, add the smallest pack that covers the remainder
  if (covered < targetM2) {
    const remainder = targetM2 - covered;
    const smallestAdequate = sorted
      .slice()
      .reverse()
      .find((p) => p.coverage_m2 >= remainder) || sorted[0];
    counts.set(smallestAdequate.label, (counts.get(smallestAdequate.label) || 0) + 1);
    covered += smallestAdequate.coverage_m2;
  }

  const picks = sorted
    .filter((p) => counts.has(p.label))
    .map((p) => ({
      ...p,
      count: counts.get(p.label),
    }));

  const totalCost = picks.reduce((s, p) => s + (p.price || 0) * p.count, 0);
  return { picks, totalCoverage: covered, totalCost, hasPrice: false };
}

/**
 * DP minimum-cost cover.
 * We discretise m² to 1-decimal precision and find the cheapest combination
 * whose coverage ≥ target. For practical pack sizes this is fast enough.
 */
function dpMinimumCost(packs, targetM2) {
  // Work in units of 0.1 m² to keep DP integer-indexed
  const SCALE = 10;
  const target = Math.ceil(targetM2 * SCALE);
  const INF = Number.POSITIVE_INFINITY;

  // We need cheapest cost where coverage >= target.
  // Use an unbounded knapsack: cost[i] = min cost to cover EXACTLY i units.
  // Then answer = min(cost[i]) for i in [target, target + maxPackCoverage].
  const maxPackCoverage = Math.ceil(Math.max(...packs.map((p) => p.coverage_m2)) * SCALE);
  const maxI = target + maxPackCoverage;
  const cost = new Array(maxI + 1).fill(INF);
  const pick = new Array(maxI + 1).fill(-1);
  cost[0] = 0;

  for (let i = 1; i <= maxI; i++) {
    for (let pi = 0; pi < packs.length; pi++) {
      const p = packs[pi];
      const cov = Math.round(p.coverage_m2 * SCALE);
      if (cov <= 0 || cov > i) continue;
      const candidate = cost[i - cov] + p.price;
      if (candidate < cost[i]) {
        cost[i] = candidate;
        pick[i] = pi;
      }
    }
  }

  // Find the cheapest i >= target
  let best = -1;
  let bestCost = INF;
  for (let i = target; i <= maxI; i++) {
    if (cost[i] < bestCost) {
      bestCost = cost[i];
      best = i;
    }
  }

  if (best === -1) {
    // Shouldn't happen if any pack is large enough, but fall back to greedy
    return greedyMinimumWaste(packs, targetM2);
  }

  // Reconstruct picks
  const counts = new Map();
  let idx = best;
  while (idx > 0 && pick[idx] !== -1) {
    const p = packs[pick[idx]];
    counts.set(p.label, (counts.get(p.label) || 0) + 1);
    idx -= Math.round(p.coverage_m2 * SCALE);
  }

  const picks = packs
    .filter((p) => counts.has(p.label))
    .map((p) => ({ ...p, count: counts.get(p.label) }))
    .sort((a, b) => b.coverage_m2 - a.coverage_m2);

  return {
    picks,
    totalCoverage: best / SCALE,
    totalCost: bestCost,
    hasPrice: true,
  };
}
